import { useState } from "react";
import api from "../lib/api";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import logo from "../assets/img/logo3.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Simular useSearchParams: ?registered=true
  const searchParams = new URLSearchParams(location.search);
  const registered = searchParams.get("registered") === "true";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { data } = await api.post("/api/auth/login", formData);

      // Guardar en localStorage
      localStorage.setItem("token", data.token || "fake-jwt-token");
      localStorage.setItem("user", JSON.stringify(data.user || { username: formData.username }));

      // Redirigir según rol
      const role = data.user?.role || "user";
      if (role === "vendor") {
        navigate("/profile");
      } else {
        // admin and regular users go to the map
        navigate("/map");
      }
    } catch (err) {
      const message = err.response?.data?.error || "Usuario o contraseña incorrectos";
      setError(message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="GeoGas Logo" className="h-12 w-auto" />
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center p-4 bg-gradient-to-b from-[#0288d0]/5 to-white">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl md:text-3xl font-bold text-center text-[#0288d0]">
              Iniciar Sesión
            </CardTitle>
            <CardDescription className="text-center text-base">
              Accede a tu cuenta GeoGas
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Mensaje de éxito al registrarse */}
            {registered && (
              <Alert className="mb-4 border-green-500 bg-green-50 text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  ¡Cuenta creada con éxito! Ahora inicia sesión.
                </AlertDescription>
              </Alert>
            )}

            {/* Error */}
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-base">
                  Usuario
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Tu nombre de usuario"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={loading}
                  autoFocus
                  className="h-12 text-base"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-base">
                  Contraseña
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  disabled={loading}
                  className="h-12 text-base"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#0288d0] hover:bg-[#0288d0]/90 text-white font-semibold text-base"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Iniciando sesión...
                  </>
                ) : (
                  "Iniciar Sesión"
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex flex-col gap-3 pt-4">
            <Link
              to="/recover-password"
              className="text-sm text-[#0288d0] hover:underline font-medium"
            >
              ¿Olvidaste tu contraseña?
            </Link>

            <div className="text-center text-sm text-muted-foreground">
              ¿No tienes cuenta?{" "}
              <Link to="/register" className="text-[#0288d0] hover:underline font-medium">
                Regístrate gratis
              </Link>
            </div>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
}